"use client";

import { Translate } from "@/components/Translate";
import { Close24 } from "@/components/icons/Close24";
import { Filter24 } from "@/components/icons/Filter24";
import { Button } from "@/components/ui/Button";
import {
	DrawerClose,
	DrawerContent,
	DrawerFooter,
	DrawerHeader,
	DrawerMain,
	DrawerRoot,
	DrawerTitle,
	DrawerTrigger,
	DrawerViewport,
} from "@/components/ui/Drawer";
import {
	PopoverClose,
	PopoverContent,
	PopoverFooter,
	PopoverRoot,
	PopoverTrigger,
} from "@/components/ui/Popover";
import { Toggle, ToggleGroup } from "@/components/ui/ToggleGroup";
import { hexToHsl } from "@/lib/utils";

import { DialogClose, clsx } from "@frend-digital/ui";
import { useUrlSearchParams } from "@frend-digital/ui/next";
import type React from "react";
import { memo, startTransition, useMemo, useOptimistic, type ComponentProps } from "react";
import styles from "../Filters.module.css";

import { Flex } from "@/components/ui/Flex";
import { CheckboxIcon, CheckboxIndicator, CheckboxRoot } from "@/components/ui/Checkbox";
import { ChevronRight } from "@/components/icons/Chevron";
import {
	isMultiColorName,
	type CustomFormattedFilter,
	type CustomFormattedFilterGroup,
	type FormattedFilterGroup,
} from "@/lib/depict/filters";
import { useProducts } from "../../ListingContext";
import { useGlobal } from "@/features/global/GlobalContext";
import type { FilterGroupStoryblok } from "@/generated/sb/sb-types";
import { splitFilter } from "../utils";
import { withSearchParams } from "@/components/withSearchParams";
import { setTimeout } from "@/utils/timers";

const ShowProductsBtn = ({ ...props }: ComponentProps<typeof Button> & {}) => {
	const { total, query } = useProducts();

	const { isFetching } = query;

	return (
		<Button
			{...props}
			loading={isFetching}
			style={{
				width: "fit-content",
			}}
		>
			<Translate
				params={{
					total,
				}}
			>
				Show products
			</Translate>
		</Button>
	);
};

export const FilterModal = memo(
	({
		filters,
		filterKeys,
	}: {
		filters: FormattedFilterGroup[];
		filterKeys: string[];
	}) => {
		return (
			<>
				<div className="desktop">
					<PopoverRoot>
						<PopoverTrigger
							className={styles.filterButton}
							type="button"
							onClick={(e) => {
								e.currentTarget?.scrollIntoView({ behavior: "smooth", block: "start" });
							}}
						>
							<Filter24 />
							<span>
								<Translate>Filter</Translate>
							</span>
						</PopoverTrigger>
						<PopoverContent className={styles.popover}>
							<Flex className={styles.header} justify="between">
								<h2 className={styles.filterTitle}>
									<Translate>Filter</Translate>
								</h2>
								<PopoverClose>
									<Close24 />
								</PopoverClose>
							</Flex>
							<div className={styles.padded}>
								<Filters filters={filters} />
							</div>
							<PopoverFooter className={styles.footer}>
								<div className={styles.buttons}>
									<PopoverClose asChild>
										<ShowProductsBtn />
									</PopoverClose>
								</div>
							</PopoverFooter>
						</PopoverContent>
					</PopoverRoot>
				</div>
				<div className="mobile">
					<DrawerRoot>
						<DrawerTrigger asChild>
							<button type="button" className={styles.filterButton}>
								<Filter24 />
								<span className="mobile">
									<Translate>Filter</Translate>
								</span>
							</button>
						</DrawerTrigger>
						<DrawerViewport side="right">
							<DrawerContent>
								<DrawerClose className={styles.close} />
								<DrawerHeader className={styles.filterHeader}>
									<DrawerTitle>
										<Translate>Filter</Translate>
									</DrawerTitle>
								</DrawerHeader>
								<DrawerMain>
									<Filters filters={filters} />
								</DrawerMain>
								<DrawerFooter className={styles.footer}>
									<DialogClose asChild>
										<ShowProductsBtn />
									</DialogClose>
								</DrawerFooter>
							</DrawerContent>
						</DrawerViewport>
					</DrawerRoot>
				</div>
			</>
		);
	},
);

FilterModal.displayName = "FilterModal";

const searchParamOptions = {
	replace: true,
	scroll: false,
};

function buildNestedFilters(
	group: FilterGroupStoryblok,
	filters: FormattedFilterGroup[],
	childrenMap: Map<string, FormattedFilterGroup[]>,
): CustomFormattedFilterGroup | null {
	const groupValues = group.filters ?? [];
	const matchedValues = groupValues.flatMap((unparsedValue) => {
		const { field, value } = splitFilter(unparsedValue);
		const parent = filters.find((f) => f.key === field);
		if (!parent) return [];

		if (value.startsWith("g_")) {
			return parent.values
				.filter((v) => v.name?.toLowerCase() === value.slice(2).toLowerCase())
				.map((match) => ({
					count: match.count,
					field,
					name: match.name,
					value: match.value,
				}));
		}

		const match = parent.values.find((v) => v.value.toLowerCase() === value.toLowerCase());
		return match ? [{ count: match.count, field, name: match.name, value: match.value }] : [];
	});

	const children = (group.subGroup ?? [])
		.map((sub) => buildNestedFilters(sub, filters, childrenMap))
		.filter(Boolean) as CustomFormattedFilterGroup[];

	if (matchedValues.length === 0 && children.length === 0) return null;

	return {
		key: "group",
		id: group._uid,
		label: String(group.filterGroupTitle ?? group.field),
		title: group.filterGroupTitle ?? "",
		values: matchedValues,
		children: children,
		checkboxOnly: group.checkboxOnly ?? undefined,
	};
}

export const Filters = memo(
	({
		filters,
	}: {
		filters: FormattedFilterGroup[];
	}) => {
		const { data } = useGlobal();
		const childrenMap = new Map<string, FormattedFilterGroup[]>();

		const enrichedFilterGroups = (data?.filterGroups ?? [])
			.map((group) => buildNestedFilters(group, filters, childrenMap))
			.filter(Boolean) as CustomFormattedFilterGroup[];

		const standardFilterKeys = ["size_name", "var_color.value"];
		const standardFilters: CustomFormattedFilterGroup[] = filters
			.filter((f) => standardFilterKeys.includes(f.key))
			.map((f) => ({
				...f,
				values: f.values.map((v) => ({
					...v,
					field: f.key,
				})),
			}));

		const combinedFilters = [...standardFilters, ...enrichedFilterGroups];
		const sizeFilter = combinedFilters.find((f) => f.key === "size_name");
		const colorFilter = combinedFilters.find((f) => f.key === "var_color.value");
		const hasStockFilter = filters?.some((filter) => filter.label === "in_stock");

		return (
			<div className={styles.filters}>
				{sizeFilter && <FilterGroup filter={sizeFilter} key={sizeFilter.id} />}
				{colorFilter && <FilterGroup filter={colorFilter} key={colorFilter.id} />}
				{hasStockFilter && <StockFilter />}
				{combinedFilters
					.filter(
						(filter) =>
							filter.label !== "in_stock" &&
							filter.key !== "size_name" &&
							filter.key !== "var_color.value",
					)
					.map((filter) => (
						<FilterGroup filter={filter} key={filter.id} />
					))}
			</div>
		);
	},
);

Filters.displayName = "Filters";
const StockFilter = () => {
	const searchParams = useUrlSearchParams(searchParamOptions);
	const stockParam = searchParams.get("in_stock");

	const [value, setOptimisticValue] = useOptimistic(stockParam !== "false");

	return (
		<fieldset className={styles.fieldset}>
			<label className={styles.stockRow} htmlFor="stock-filter-checkbox">
				<span className={styles.stockLabel}>
					<Translate>In stock</Translate>
				</span>
				<CheckboxRoot
					checked={value}
					onCheckedChange={(checked) => {
						startTransition(async () => {
							setOptimisticValue(checked);

							// Set timeout to allow transition of optimistic UI to happend before updating search params
							await setTimeout(1);

							if (checked) {
								searchParams.delete("in_stock");
							} else {
								searchParams.set("in_stock", "false");
							}
						});
					}}
				>
					<CheckboxIndicator>
						<CheckboxIcon />
					</CheckboxIndicator>
				</CheckboxRoot>
			</label>
		</fieldset>
	);
};

const FilterGroup = memo(
	({
		filter,
		isSubGroup,
	}: {
		filter: CustomFormattedFilterGroup;
		isSubGroup?: boolean;
	}) => {
		const searchParams = useUrlSearchParams(searchParamOptions);
		const [value, setOptimisticValue] = useOptimistic(searchParams.getAll(filter.key));

		const subGroups = filter.children || [];

		const handleValueChange = (values: string[]) => {
			const filteredValues = values.filter((value) => filter.values.some((v) => v.value === value));
			const current = searchParams.getAll(filter.key);
			startTransition(async () => {
				setOptimisticValue(filteredValues);
				// Set timeout to allow transition of optimistic UI to happend before updating search params
				await setTimeout(1);

				// Clear all values
				for (const value of current) {
					searchParams.delete(filter.key, value);
				}

				// Add all values
				for (const value of filteredValues) {
					searchParams.append(filter.key, value);
				}
			});
		};

		const children = useMemo(() => {
			if (filter.key === "size_name") {
				return (
					<>
						{filter?.values?.map((value) => (
							<Toggle key={filter.key + value.value} value={value.value} className={styles.size}>
								<span className="lh-reset">
									<Translate>{value.name}</Translate>
								</span>
							</Toggle>
						))}
					</>
				);
			}

			if (filter.key === "var_color.value") {
				return (
					<>
						{filter.values.map((color) => {
							const hsl = color.hex ? hexToHsl(color.hex) : null;
							const isMultiColor = isMultiColorName(color.name);

							if (isMultiColor) {
								const colors = [
									"#FF0000",
									"#FF7F00",
									"#FFFF00",
									"#00FF00",
									"#0000FF",
									"#4B0082",
									"#9400D3",
								];
								const gradientColors = colors.join(", ");

								return (
									<Toggle
										key={filter.key + color.value}
										value={color.value}
										circular
										className={clsx(styles.color, styles.multiColor)}
										style={
											{ "--gradient": `conic-gradient(${gradientColors})` } as React.CSSProperties
										}
										aria-label={color.name}
									>
										<span />
									</Toggle>
								);
							}

							return (
								<Toggle
									key={filter.key + color.value}
									value={color.value}
									circular
									className={clsx(styles.color, (hsl?.l ?? 0) > 80 && styles.light)}
									style={{ "--color": color.hex } as React.CSSProperties}
									aria-label={color.name}
								>
									<span />
								</Toggle>
							);
						})}
					</>
				);
			}

			return (
				<div style={{ flexDirection: "column" }}>
					{filter.values?.map((val) => {
						return <CheckboxFilter filter={filter} filterValue={val} key={val.field + val.value} />;
					})}
				</div>
			);
		}, [filter]);

		return (
			<>
				{filter.checkboxOnly ? (
					<fieldset className={styles.fieldsetCheckbox}>
						<div className={styles.content}>
							{filter.values?.map((val) => (
								<CheckboxFilter
									filter={filter}
									filterValue={val}
									key={val.field + val.value}
									className={styles.checkboxOnly}
								/>
							))}
						</div>
					</fieldset>
				) : (
					<details
						key={filter.key}
						open={filter.key === "size_name" || filter.key === "var_color.value"}
						className={styles.fieldset}
					>
						<summary className={clsx(styles.legend, isSubGroup && styles.subGroup)}>
							<Translate>{filter.title || filter.values[0].name}</Translate>
							<ChevronRight className={styles.indicator} />
						</summary>
						<div className={styles.content}>
							{filter.values.length > 0 &&
							(filter.key === "size_name" || filter.key === "var_color.value") ? (
								<ToggleGroup
									value={value}
									toggleMultiple
									onValueChange={handleValueChange}
									className={styles.toggleGroup}
								>
									{children}
								</ToggleGroup>
							) : (
								children
							)}

							{subGroups.map((sub) => (
								<FilterGroup key={sub.id} filter={sub} isSubGroup={true} />
							))}
						</div>
					</details>
				)}
			</>
		);
	},
);

FilterGroup.displayName = "FilterGroup";

const CheckboxFilter = withSearchParams<{
	filterValue: CustomFormattedFilter;
	filter: CustomFormattedFilterGroup;
	className?: string;
}>(({ searchParams, filterValue: val, className }) => {
	const encoded = useMemo(() => encodeURIComponent(val.value), [val.value]);
	const [isChecked, setChecked] = useOptimistic(searchParams.has(val.field, encoded));
	const checkboxId = `checkbox-${val.field}-${encoded}`;

	return (
		<label
			key={val.field + encoded}
			htmlFor={checkboxId}
			className={clsx(styles.checkboxRow, className)}
		>
			<span className="lh-reset">
				<Translate>{val.name}</Translate>
			</span>

			<CheckboxRoot
				id={checkboxId}
				checked={isChecked}
				onCheckedChange={(checked) => {
					startTransition(async () => {
						setChecked(checked);
						// Set timeout to allow transition of optimistic UI to happend before updating search params
						await setTimeout(1);

						if (checked) {
							searchParams.append(val.field, encoded);
						} else {
							searchParams.delete(val.field, encoded);
						}
					});
				}}
			>
				<CheckboxIndicator>
					<CheckboxIcon />
				</CheckboxIndicator>
			</CheckboxRoot>
		</label>
	);
});
